<template>
  <div class="mt-4">
    <CitySelectBlock
      :city-select-bottom-sheet-name="CITY_SELECT_BOTTOM_SHEET"
      :orig-city-name="origCityName"
      :is-loading="isOffersLoading"
      :offers="offers"
      @onCitySelect="handleCitySelect"
    />
    <template v-if="offers?.length > 0">
      <OffersVerticalListBlock
        v-if="isEvent && hasSameDestinationCity"
        :offers="offers"
        :is-loading="isOffersLoading"
        :current-special-offer="currentSpecialOffer"
        :has-same-destination-city="isEvent && hasSameDestinationCity"
        :cashback-click-source="
          analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource
            .SPECIAL_OFFERS
        "
        @onCashbackClick="$emit('onCashbackClick')"
      />
      <div v-else class="space-y-6">
        <OffersCarouselBlock
          v-for="(result, i) in groupedOffers"
          :key="i"
          :offers="result.offers"
          :is-loading="isOffersLoading"
          :current-special-offer="currentSpecialOffer"
          :source="`specialOffers.details.${result.city.iata_code}`"
          :cashback-click-source="
            analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource
              .SPECIAL_OFFERS
          "
          @onCashbackClick="$emit('onCashbackClick')"
        >
          <CarouselTitle
            v-if="result.country"
            :is-single-offer="result.offers?.length === 1"
            :country="result.country"
            :city="result.city"
            :show-city="true"
            :flag-size="20"
            @onShowAllOffers="() => handleShowAllOffers(result.city.name)"
          />
        </OffersCarouselBlock>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { MappedLocation, specialOffers } from 'shared';
  import { uniq } from 'lodash-es';

  import {
    OffersCarouselBlock,
    OffersVerticalListBlock,
    CitySelectBlock,
    CarouselTitle,
  } from '@/features/offers';
  import { specialOffersModel } from '@/features/special-offers';

  import type { hotOffersTypes } from '@/entities/hot-offers';

  import { analyticsEventsLib } from '@/shared/lib';

  const props = defineProps<{
    offers: hotOffersTypes.MappedHotOffer[];
    isOffersLoading: boolean;
    currentSpecialOffer?: specialOffers.Special;
  }>();

  const emit = defineEmits<{
    onCashbackClick: [];
    onShowAllOffers: [
      cityName: string,
      offers: hotOffersTypes.MappedHotOffer[],
    ];
  }>();

  const { origCityName, changeOrigCity } =
    specialOffersModel.useSpecialOffers();
  const CITY_SELECT_BOTTOM_SHEET = 'special-offer-city-select';
  const hasSameDestinationCity = computed(() => {
    if (props.offers.length === 0) return false;

    const firstDestinationCityName = props.offers[0]?.destination.city.name;
    return props.offers.every(
      offer => offer.destination.city.name === firstDestinationCityName,
    );
  });
  const isEvent = computed(() => props.currentSpecialOffer?.type === 'EVENT');

  const handleCitySelect = (city: MappedLocation) => {
    changeOrigCity(city);
  };

  const handleShowAllOffers = (cityName: string) => {
    const cityOffers = props.offers.filter(
      offer => offer.destination.city.name === cityName,
    );
    emit('onShowAllOffers', cityName, cityOffers);
  };

  const groupedOffers = computed(() => {
    const citiesNames = uniq(
      props.offers.map(offer => offer.destination.city.name),
    );

    return citiesNames.map(cityName => {
      const cityOffer = props.offers.filter(
        offer => offer.destination.city.name === cityName,
      )[0];

      return {
        country: {
          ...cityOffer.destination.country,
          code: cityOffer.destination.country.iso_code,
        },
        city: {
          ...cityOffer.destination.city,
        },
        offers: props.offers.filter(
          offer => offer.destination.city.name === cityName,
        ),
      };
    });
  });
</script>
